import { PaymentType, UserType } from '@/types';
import { once } from 'lodash-es';

export type GTMEventCallback = () => void;

export type TrackingParameterValue = string | number | undefined | GTMEventCallback;

export interface TrackingEvent {
  eventCallback?: GTMEventCallback;
  eventTimeout?: number;
  [key: string]: TrackingParameterValue;
}

export interface PageTrackingParameters {
  event?: string;
  eventCallback?: GTMEventCallback;
  eventTimeout?: number;
  h_event_category_006?: string;
  h_event_action_007?: string;
  h_event_label_008?: string;
  h_feature_group_001?: string;
  h_web_feature_id_019?: string;
  h_transaction_id_042?: string;
  h_transaction_total_017?: number;
  h_transaction_type_063?: string;
  h_product_matching_id_038?: string;
  h_purchase_list_items_all_173?: string;
  h_page_type_110?: string;
  h_page_subtype_112?: string;
  h_content_language_116?: string;
  h_client_login_user_type_037?: string;
  h_client_login_user_id_036?: string;
  h_login_status_035?: string;
  h_listing_geo_canton_157?: string;
  h_listing_categories_hgrets_118?: string;
  h_listing_geo_country_158?: string;
  h_listing_geo_tags_169?: string;
  h_listing_offer_type_117?: string;
  h_listing_geo_zip_152?: string;
  h_listing_draft_id_168?: string;
  h_listing_lister_id_154?: string;
  h_listing_picture_count_155?: number;
  h_listing_object_ref_id_123?: string;
  h_listing_status_121?: string;
  h_advertiser_type_040?: string;
  h_user_group_039?: string;
  h_experiment_split_treatments_107?: string;
  h_list_item_clicked_id_085?: string;
  h_list_name_086?: string;
  h_list_items_all_087?: string;
  h_list_items_loaded_088?: string;
  h_position_on_list_overall_283?: number;
  h_list_items_total_284?: number;
  h_list_items_loaded_285?: number;
  h_extra_information_144?: string;
  h_listing_advertisement_id_120?: string;
  h_client_login_provider_034?: string;
  h_login_location_049?: string;
  h_conversion_source_061?: string;
  h_target_url_029?: string;
}

export enum GTMPageType {
  FUNNEL_PAGE = 'account_insertListing',
  MY_ADS_PAGE = 'account_manage',
  LANDING_PAGE = 'insertListing',
  ACCOUNT_HUB = 'account_hub',
  DASHBOARD = 'account_dashboard',
}

export const FunnelPageSubType: Record<string, string> = {
  details: 'input_details',
  bundles: 'input_bundle',
  billing: 'input_billing',
  confirmation: 'input_confirmation',
};
export const tenantProofReminderPageSubType = 'input_tenant_proof_reminder';
export const myAdsPageSubType = 'myAds';

export enum LoginStatus {
  LOGGED_IN = 'loggedin',
  NOT_LOGGED_IN = 'notLoggedin',
}

export enum EVENT {
  VIRTUAL_PAGEVIEW = 'virtual_pageview',
  INSERTION_INPUT_START = 'insertion_input_start',
  PURCHASE = 'purchase',
  CTA_CLICK = 'cta_click',
  BUTTON_CLICK = 'button_click',
  INSERTION_INTERACTION = 'insertion_interaction',
  HUB_INTERACTION = 'hub_interaction',
  FEATURE_DISPLAYED = 'feature_displayed',
  LIST_LOAD = 'list_load',
  LIST_CLICKTHROUGH = 'list_clickthrough',
  AUTHENTICATION = 'authentication',
}

export enum EVENT_CATEGORY {
  HUB_INTERACTION = 'hub_interaction',
  INSERTION_INTERACTION = 'insertion_interaction',
}

export enum BILLING_EVENT_ACTION {
  PUBLISH_PPA_LISTING = 'publish_ppa_listing',
}

export enum MY_ADS_EVENT_ACTION {
  OPEN_INSERT_AD = 'open_insertAd',
  DELETE_RULE = 'deleteRule',
  CONFIRM_DEACTIVATE_AD = 'confirm_deactivate_ad',
  CONFIRM_SCHEDULE_DEACTIVATE_AD = 'confirm_schedule_deactivate_ad',
  REMOVE_SCHEDULE_DEACTIVATE_AD_TRY = 'remove_schedule_deactivate_ad_try',
  REMOVE_SCHEDULE_DEACTIVATE_AD = 'remove_schedule_deactivate_ad',
  OPEN_MESSAGES = 'open_messages',
  EDIT_AD = 'edit_ad',
  VISIT_AD = 'visit_ad',
  EDIT_INVOICE_DETAILS = 'edit_invoice_details',
  DUPLICATE_AD = 'duplicate_ad',
  DEACTIVATE_AD_TRY = 'deactivate_ad_try',
  SCHEDULE_DEACTIVATE_AD_TRY = 'schedule_deactivate_ad_try',
  SELECT_DEACTIVATE_AD_DATE = 'select_deactivate_ad_date',
  DELETE_AD_TRY = 'delete_ad_try',
  PREVIEW_AD = 'preview_ad',
  REACTIVATE_AD = 'reactivate_ad',
  UPGRADE_BUNDLE = 'upgrade_bundle',
  CONTINUE_TO_REVIEW_CONFIRM = 'continue_to_review_confirm',
  BOOK_TL = 'book_TL',
  OPEN_CONTACT_SEEKERS_UPGRADE = 'open_contact_seeker_upgrade',
  CHECKOUT_CONTACT_SEEKERS_UPGRADE = 'checkout_contact_seeker_upgrade',
  OPEN_VIEWING_APPOINTMENT = 'open_viewing_appointment',
  CHECKOUT_VIEWING_APPOINTMENT = 'checkout_viewing_appointment',
}

export enum BUNDLE_PAGE_EVENT_ACTION {
  SUBMIT_CROSSLISTING_OPTION = 'submit_crosslisting_option',
  DISPLAY_DOCUMENT_UPLOAD_MODAL = 'display_document_upload_modal',
  SWITCH_PPA_PACKAGE = 'switch_ppa_package',
  CONTINUE_WITH_BASIC_PACKAGE = 'continue_with_basic_package',
}

export enum TENANT_PROOF_EVENT_ACTION {
  UPLOAD_TENANT_PROOF = 'upload_tenant_proof',
}

export const DOCUMENT_UPLOAD_MODAL_EVENT_LABEL = 'document_upload_modal';

export enum CLIENT_LOGIN_USER_TYPE {
  PPA_SEMIPRO = 'ppa_semipro',
  PPA_PRIVATE = 'ppa_private',
  PPA_CCC = 'ppa_ccc',
  PPA_GUEST = 'ppa_guest',
}

export enum EVENT_AUTHENTICATION_ACTION {
  SIGN_UP = 'auth_sign_up',
  LOGIN = 'auth_login',
}
export enum EVENT_AUTHENTICATION_LABEL {
  IS24 = 'immoscout24_web',
  HG = 'homegate_web',
}
export const LOGIN_LOCATION = 'ppa';

export const userTypeMap: Record<UserType, string> = {
  [UserType.PRIVATE]: CLIENT_LOGIN_USER_TYPE.PPA_PRIVATE,
  [UserType.TENANT]: CLIENT_LOGIN_USER_TYPE.PPA_PRIVATE,
  [UserType.OWNER]: CLIENT_LOGIN_USER_TYPE.PPA_PRIVATE,
  [UserType.SEMI_PRO]: CLIENT_LOGIN_USER_TYPE.PPA_SEMIPRO,
  [UserType.BUSINESS]: CLIENT_LOGIN_USER_TYPE.PPA_SEMIPRO,
};

export const PaymentTypeToGTMExtraInformation: Record<PaymentType, string> = {
  [PaymentType.DIRECT]: 'credit_card_payment',
  [PaymentType.INVOICE]: 'invoice_payment',
};

export enum CrosslistingGTMExtraInformation {
  CROSSLISTING = `crosslisting=true`,
  NON_CROSSLISTING = `crosslisting=false`,
}

export enum NewConstructionGTMExtraInformation {
  NEW_CONSTRUCTION = `new_construction_add_on=true`,
  NO_NEW_CONSTRUCTION = `new_construction_add_on=false`,
}

export const isGTMEnabled = (): boolean => !!window.dataLayer;

export function trackEvent(eventParams: PageTrackingParameters): void {
  const event: TrackingEvent = { ...eventParams };

  // callback handling - needs to be fired even if tracking blockers are active
  if (event.eventCallback) {
    // make sure eventCallback is only called once, and never again by any fallback timeout
    const eventCallbackOnce = once(event.eventCallback);

    // this happens when Google Tag Manager is not loaded (e.g. in development without SSR or blocked)
    if (!isGTMEnabled()) {
      eventCallbackOnce();
      return;
    }

    // Extend the event params
    event.eventCallback = eventCallbackOnce;
    event.eventTimeout = event?.eventTimeout ?? 1000;

    // if eventCallback is not executed within timeout the callback is executed manually
    window.setTimeout(eventCallbackOnce, event.eventTimeout + 50);
  }

  if (isGTMEnabled()) {
    window.dataLayer.push(event);
  }
}

export const getUserIdForTracking = (
  userId: string | undefined,
  isGuest: boolean,
): string | undefined => {
  // Don't track user ID for guest users
  if (isGuest) {
    return undefined;
  }

  return userId || undefined;
};
